<template>
  <el-select
    v-model="value"
    :class="{ desktop: isDesktop }"
    multiple
    filterable
    :disabled="disabled"
    remote
    reserve-keyword
    placeholder="Enter 2 or more characters"
    no-data-text="Could not find and spots matching your query"
    no-match-text="Could not find any spots for your query"
    loading-text="Loading..."
    :remote-method="lookup"
    :loading="loading"
    @update:modelValue="update"
  >
    <el-option v-if="options.length > 0" key="all" label="All" :value="-1" />
    <el-option
      v-for="item in options"
      :key="item.id"
      :label="item.text"
      :value="item.id"
    />
  </el-select>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  reactive,
  onMounted,
  watchEffect,
  watch
} from 'vue'
import { ISpotsPayload, ISpot } from '@/apps/traforama/interfaces/spots'
import { useSpotsStore } from '@/apps/traforama/store/spots'
import useResize from '@/hooks/useResize'

interface Option {
  id: number
  text: string
}

export default defineComponent({
  name: 'SpotsSelector',
  props: {
    modelValue: {
      type: Array,
      required: true
    },
    stream: {
      type: Number,
      default: 0
    },
    typeId: {
      type: Number,
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    },
    ignoreStreamFilter: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const value = ref(props.modelValue)
    const spotsStore = useSpotsStore()
    const loading = ref(false)
    const options = reactive<Array<Option>>([])
    const currentQuery = ref('')

    const loadInitial = async (leftList: boolean) => {
      if (props.modelValue.length < 1) {
        return
      }
      const payload = {
        filter: {
          id: props.modelValue as Array<number>
        },
        limit: props.modelValue.length,
        start: 0
      } as ISpotsPayload
      const response = await spotsStore.SearchItems(payload)
      assignOptions(response, leftList)
    }

    watch(
      (_: any) => props.modelValue,
      (newValue: any, oldVal: any) => {
        value.value = newValue
        if (props.modelValue.length < 1) {
          assignOptions([], false)
        } else {
          loadInitial(false)
        }
      }
    )

    const assignOptions = (items: Array<ISpot>, leftList: boolean) => {
      const newOptions = items.map((item: ISpot) => {
        return { id: Number(item.id), text: item.attributes.name } as Option
      })
      if (leftList) {
        if (newOptions.length > options.length) {
          options.splice(0, newOptions.length, ...newOptions)
        }
      } else {
        options.splice(0, options.length, ...newOptions)
      }
    }

    const lookup = async (query: string) => {
      currentQuery.value = query
      if (query.length >= 2) {
        loading.value = true
        const filter = {
          name__or__id: query,
          type_id: props.typeId
        } as Record<string, any>

        if (!props.ignoreStreamFilter) {
          filter.stream = props.stream
        }

        const payload = {
          filter,
          limit: 20,
          start: 0
        } as ISpotsPayload
        const response = await spotsStore.SearchItems(payload)
        assignOptions(response, false)
        loading.value = false
      } else {
        options.splice(0, options.length, ...[])
      }
    }

    const update = (newValue: any) => {
      if (newValue.includes(-1)) {
        const allSpotIds = options.map((opt) => opt.id)
        const filteredValue = newValue.filter((v: number) => v !== -1)
        const mergedValue = [...new Set([...filteredValue, ...allSpotIds])]
        value.value = mergedValue
        context.emit('update:modelValue', mergedValue)
      } else {
        context.emit('update:modelValue', newValue)
      }
    }

    const { isDesktop } = useResize()

    loadInitial(false)

    return { loading, lookup, options, update, isDesktop, value }
  }
})
</script>

<style lang="scss" scoped>
.el-select {
  width: 100%;
}
</style>
